/**
 * Texto puro de documento (o regulamento publicado como .txt, spec 017) virando estrutura pra ler na tela: titulos,
 * paragrafos, itens numerados, marcadores e os quadros "Campo: valor" dos planos. Pedido do Vitor (13/10): o link do
 * regulamento no checkout abria o texto cru, e ele queria a mesma leitura da politica de privacidade.
 *
 * O texto e o que foi aceito (o `sha256` e dele) e nao muda: aqui so se decide como mostrar. Ele chega de dois jeitos,
 * e as duas regras convivem:
 *  - copiado do PDF: linha quebrada no meio da frase (~80 letras) e linha vazia entre paragrafos;
 *  - escrito a mao: uma linha por paragrafo, sem linha vazia.
 * Nada e inventado: toda palavra do texto aparece, na mesma ordem. So o titulo repetido da quebra de pagina do PDF sai.
 */

export type ItemNumerado = { numero: number; texto: string; filhos: BlocoDeDocumento[] };
export type BlocoDeDocumento =
  | { tipo: "secao"; texto: string }
  /** "2.1. Inscrição e cobrança do plano": a subsecao numerada, abaixo da secao. */
  | { tipo: "subsecao"; texto: string }
  | { tipo: "subtitulo"; texto: string }
  /** Linhas com "|": a primeira e o cabecalho (13/10, as tabelas do regulamento da Global). */
  | { tipo: "tabela"; cabecalho: string[]; linhas: string[][] }
  | { tipo: "paragrafo"; texto: string }
  | { tipo: "marcadores"; itens: string[] }
  | { tipo: "campos"; linhas: { campo: string; valor: string }[] }
  | { tipo: "numerada"; itens: ItemNumerado[] };

/** Linha que termina frase. */
const fecha = (l: string) => /[.:;!?]$/.test(l);
const ITEM = /^(\d{1,2})[.)]\s+(.*)$/;
/** "2.1. Inscrição e cobrança do plano" ou "4.8 Ativos": numero de subsecao, curta, sem fim de frase. */
const SUBSECAO = /^\d{1,2}\.\d{1,2}\.?\s+[A-ZÀ-Ý]/;
/** "- Ser pessoa física;": marcador escrito como marcador. */
const MARCADOR = /^[-•]\s+(\S.*)$/;
/** "Plano | Meta | Drawdown": linha de tabela, duas ou mais celulas. */
const celulas = (l: string) => (l.includes("|") ? l.split("|").map((c) => c.trim()) : null);
/** "Meta de aprovação: R$800,00": campo curto, dois-pontos e o valor na mesma linha, sem ponto final. */
const CAMPO = /^([A-ZÀ-Ý][^:]{1,44}):\s+(\S.*)$/;
const maiuscula = (l: string) => /^[A-ZÀ-Ý0-9"“(]/.test(l);
/** "START", "INTERMEDIÁRIO": nome de plano ou de bloco, todo em maiusculas e curto. */
const emCaixaAlta = (l: string) => l.length <= 40 && /[A-ZÀ-Ý]{2}/.test(l) && l === l.toLocaleUpperCase("pt-BR") && !/[.;!?]$/.test(l);
/**
 * Linha que o PDF quebrou: comprida e sem fim de frase. A de baixo continua ela. A linha vazia nao ajuda: o PDF poe
 * linha vazia ate no meio da frase ("1. O candidato receberá ... diretamente" / "" / "na plataforma de negociação").
 */
const quebrada = (l: string) => l.length >= 50 && !fecha(l);
/** Titulo partido no meio: "Política de" / "Cancelamento", "Como realizar" / "a inscrição?". */
const tituloPelaMetade = (ant: string, l: string) =>
  ant.length <= 40 && l.length <= 40 && !fecha(ant) && !ITEM.test(ant) && !ITEM.test(l) && maiuscula(ant) && (/^[a-zà-ý]/.test(l) || /\s(de|da|do|das|dos|e|a|o|para|com)$/i.test(ant));

/** "Fórmula de cálculo - demais repasses:": frase curta que abre o que vem embaixo comeca coisa nova, nao continua a de cima. */
const abreAlgo = (l: string) => maiuscula(l) && /:$/.test(l) && l.length <= 50;

/** Marcadores em serie comecam igual ("estejam envolvidas..." / "estejam vinculadas..."): e o proximo, nao a continuacao. */
const paralela = (ant: string, l: string) => /^[a-zà-ý]/.test(l) && ant.split(" ")[0] === l.split(" ")[0];

/** "(Mesa Real) - (10%) = Valor líquido": a conta acabou na linha. So continua se a quebra caiu logo depois do "=". */
const formulaFechada = (l: string) => l.includes("=") && !/=$/.test(l);

/** Linha de tabela, marcador ou subsecao: estrutura escrita de proposito, nunca continuacao de frase. */
const estrutura = (l: string) => Boolean(celulas(l)) || MARCADOR.test(l) || SUBSECAO.test(l);

type Linha = { texto: string; titulo: boolean };

/** Junta as quebras do PDF e limpa os espacos que o negrito do PDF deixou ("a conta será  cancelada ."). */
function linhasLogicas(bruto: string): Linha[] {
  const cruas = bruto.replace(/\r\n?/g, "\n").trim().split("\n");
  // Sem nenhuma linha vazia, o texto foi escrito com uma linha por paragrafo: nada a juntar (uma linha comprida sem
  // ponto ali e um marcador, como "tenham sido condenadas ... Sistema Financeiro Nacional").
  const veioDoPdf = cruas.some((l) => !l.trim());
  const saida: Linha[] = [];
  // A quebra se mede na ultima linha FISICA: "ao Sistema Financeiro Nacional" fecha o marcador mesmo que, junto com a de
  // cima, a frase ja seja comprida.
  let fisica = "";
  const limpa = (l: string) => l.replace(/\s+/g, " ").replace(/\s+([.,;:!?])(?=\s|$)/g, "$1").trim();
  const cheias = cruas.map(limpa).filter(Boolean);
  for (const [i, l] of cheias.entries()) {
    // "Planos BLACK" com "START BLACK" embaixo e titulo, nao o fim da frase de cima.
    const abreBloco = l.length <= 40 && !fecha(l) && Boolean(cheias[i + 1]) && (emCaixaAlta(cheias[i + 1]!) || CAMPO.test(cheias[i + 1]!));
    const ant = saida[saida.length - 1];
    const anterior = fisica;
    fisica = l;
    if (veioDoPdf && ant && !ant.titulo && tituloPelaMetade(ant.texto, l)) {
      ant.texto = `${ant.texto} ${l}`;
      ant.titulo = true;
    } else if (veioDoPdf && ant && !ant.titulo && quebrada(anterior) && !CAMPO.test(anterior) && !estrutura(anterior) && !estrutura(l) && !formulaFechada(anterior) && !ITEM.test(l) && !CAMPO.test(l) && !emCaixaAlta(l) && !abreAlgo(l) && !abreBloco && !paralela(ant.texto, l)) {
      ant.texto = `${ant.texto} ${l}`;
    } else saida.push({ texto: l, titulo: false });
  }
  return saida;
}

export function blocosDoTexto(bruto: string, opcoes: { titulo?: string } = {}): BlocoDeDocumento[] {
  const linhas = linhasLogicas(bruto);
  // A primeira linha repetindo o titulo do documento ("REGULAMENTO GERAL - MTC PROP") ja esta no cabecalho da pagina.
  const igual = (a: string, b: string) => a.normalize("NFD").replace(/[^\p{L}\p{N}]/gu, "").toLowerCase() === b.normalize("NFD").replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
  if (opcoes.titulo && linhas[0] && igual(linhas[0].texto, opcoes.titulo)) linhas.shift();

  const raiz: BlocoDeDocumento[] = [];
  let lista: Extract<BlocoDeDocumento, { tipo: "numerada" }> | null = null;
  let ultimaSecao: string | null = null;
  let depoisDeDoisPontos = false;
  let ultimoMarcadorComPontoEVirgula = false;
  /** Onde o bloco entra: dentro do ultimo item, enquanto a lista numerada esta aberta (o texto entre o item 6 e o 7 e do 6). */
  const destino = () => (lista ? lista.itens[lista.itens.length - 1]!.filhos : raiz);
  const ultimoDe = (alvo: BlocoDeDocumento[]) => alvo[alvo.length - 1];
  const fecharLista = () => { lista = null; };

  // Documento com subsecoes numeradas ("2.1.") mostra o numero das secoes tambem ("2. Inscrição"), senao a ordem some.
  const temSubsecao = linhas.some((l) => SUBSECAO.test(l.texto) && l.texto.length <= 80 && !/[.;!?]$/.test(l.texto));

  linhas.forEach(({ texto: l, titulo }, i) => {
    const proxima = linhas[i + 1]?.texto;
    // Tabela: a primeira linha com "|" e o cabecalho; as seguintes, com o mesmo numero de celulas, as linhas.
    const cs = celulas(l);
    if (cs && cs.length >= 2) {
      const alvo = destino();
      const ult = ultimoDe(alvo);
      if (ult?.tipo === "tabela" && ult.cabecalho.length === cs.length) ult.linhas.push(cs);
      else alvo.push({ tipo: "tabela", cabecalho: cs, linhas: [] });
      depoisDeDoisPontos = false;
      return;
    }
    const marcador = MARCADOR.exec(l);
    if (marcador) {
      const alvo = destino();
      const ult = ultimoDe(alvo);
      if (ult?.tipo === "marcadores") ult.itens.push(marcador[1]!);
      else alvo.push({ tipo: "marcadores", itens: [marcador[1]!] });
      return;
    }
    if (SUBSECAO.test(l) && l.length <= 80 && !/[.;!?]$/.test(l)) {
      // O PDF e a copia do site repetem a subsecao (o "4.3." da Global saiu duas vezes): a segunda nao abre outra.
      if (ultimoDe(raiz)?.tipo === "subsecao" && (ultimoDe(raiz) as { texto: string }).texto === l) return;
      fecharLista();
      raiz.push({ tipo: "subsecao", texto: l });
      depoisDeDoisPontos = false;
      return;
    }
    const item = ITEM.exec(l);
    if (item) {
      const [, n, resto] = item as unknown as [string, string, string];
      // "1. Requisitos para inscrição" e titulo de secao; "1. Ser pessoa física..." e item.
      const ehSecao = resto.length <= 52 && !fecha(resto) && maiuscula(resto) && !(proxima && /^[a-zà-ý]/.test(proxima));
      if (ehSecao) {
        fecharLista();
        raiz.push({ tipo: "secao", texto: temSubsecao ? `${n}. ${resto}` : resto });
        ultimaSecao = resto;
        depoisDeDoisPontos = false;
        return;
      }
      if (!lista) raiz.push((lista = { tipo: "numerada", itens: [] }));
      lista.itens.push({ numero: Number(n), texto: resto, filhos: [] });
      depoisDeDoisPontos = /:$/.test(resto);
      ultimoMarcadorComPontoEVirgula = false;
      return;
    }

    // Depois de ":", as linhas curtas que nao fecham frase sao marcadores daquela frase. A ultima de uma serie com ";"
    // costuma fechar com ponto ("Ter mais de 18 anos;" ... "Preencher as demais condições.").
    // "Planos BLACK" com "START BLACK" (ou um "Campo: valor") embaixo abre o quadro: e titulo, nunca marcador.
    const abreQuadro = l.length <= 40 && !fecha(l) && Boolean(proxima) && (emCaixaAlta(proxima!) || CAMPO.test(proxima!));
    const podeSerMarcador = l.length <= 140 && (!/\.$/.test(l) || ultimoMarcadorComPontoEVirgula) && !/:$/.test(l) && !CAMPO.test(l) && !emCaixaAlta(l) && !abreQuadro;
    if (depoisDeDoisPontos && podeSerMarcador && !titulo) {
      const alvo = destino();
      const ult = ultimoDe(alvo);
      if (ult?.tipo === "marcadores") ult.itens.push(l);
      else alvo.push({ tipo: "marcadores", itens: [l] });
      ultimoMarcadorComPontoEVirgula = /;$/.test(l);
      if (/\.$/.test(l)) depoisDeDoisPontos = false;
      return;
    }
    ultimoMarcadorComPontoEVirgula = false;

    const campo = CAMPO.exec(l);
    if (campo && !/\.$/.test(l)) {
      const alvo = destino();
      const ult = ultimoDe(alvo);
      const linha = { campo: campo[1]!.trim(), valor: campo[2]!.trim() };
      if (ult?.tipo === "campos") ult.linhas.push(linha);
      else alvo.push({ tipo: "campos", linhas: [linha] });
      depoisDeDoisPontos = false;
      return;
    }

    if (emCaixaAlta(l) && l.length <= 24) {
      fecharLista();
      raiz.push({ tipo: "subtitulo", texto: l });
      depoisDeDoisPontos = false;
      return;
    }

    // "Sem perda diária no teste" no meio do quadro do plano: e uma linha do quadro, sem valor.
    const quadro = ultimoDe(destino());
    if (quadro?.tipo === "campos" && proxima && CAMPO.test(proxima) && l.length <= 60 && !fecha(l)) {
      quadro.linhas.push({ campo: l, valor: "" });
      return;
    }

    const ehTitulo = titulo || l.length <= 60 && maiuscula(l) && (!fecha(l) || /\?$/.test(l)) && i < linhas.length - 1;
    if (ehTitulo) {
      // O PDF repete o titulo da secao no alto de cada pagina: a secao continua, a lista tambem.
      if (ultimaSecao && l.toLowerCase() === ultimaSecao.toLowerCase()) return;
      fecharLista();
      raiz.push({ tipo: "secao", texto: l });
      ultimaSecao = l;
      depoisDeDoisPontos = false;
      return;
    }

    destino().push({ tipo: "paragrafo", texto: l });
    depoisDeDoisPontos = /:$/.test(l);
  });
  return raiz;
}

/** Pedacos de uma linha com o que vira link: endereco da web e telefone (WhatsApp), que no PDF eram texto morto. */
export type TrechoDeTexto = { texto: string; href?: string };
export function trechosComLinks(texto: string): TrechoDeTexto[] {
  const achado = /(https?:\/\/[^\s]+[^\s.,;:)])|(\b(?:www\.)[^\s]+[^\s.,;:)])|\((\d{2})\)\s?(\d{4,5})-?(\d{4})/g;
  const saida: TrechoDeTexto[] = [];
  let de = 0;
  for (const m of texto.matchAll(achado)) {
    if (m.index! > de) saida.push({ texto: texto.slice(de, m.index) });
    const href = m[1] ? m[1] : m[2] ? `https://${m[2]}` : `https://wa.me/55${m[3]}${m[4]}${m[5]}`;
    saida.push({ texto: m[0], href });
    de = m.index! + m[0].length;
  }
  if (de < texto.length) saida.push({ texto: texto.slice(de) });
  return saida;
}
